/**
 * The audit (`audit`, §6.3, §5.27) — whether each hand-back is audited on its
 * own and which findings go back to the agent, and the pair the Change Map is
 * drawn with, and what holds a merge — as the app's Audit page draws it. Five values in one setting,
 * so the row has no value column and this is the value, under its line.
 *
 * The clamp is the app's own (`@airlock/contracts/settings/audit`): what leaves
 * this field is exactly what a member's machine and the Worker keep.
 */
import type React from 'react';
import { audit } from '@airlock/contracts/settings/audit';
import { DEFAULT_SETTINGS } from '@airlock/contracts/settings/defaults';
import { MODEL_EFFORT_OPTIONS, pairOf, splitPair } from '@airlock/contracts/settings/models';
import type { AuditSettings } from '@airlock/contracts/settings/types';
import { color } from '../../theme/tokens';
import { SettingSelect } from '../../components/settings-row';

const AUTO_OPTIONS = [
  { value: 'serious', label: 'High and Medium' },
  { value: 'all', label: 'Every finding' },
  { value: 'off', label: 'Off' }
];

const PICK = 'Automatic';

const RULES_OPTIONS = [
  { value: 'enforced', label: 'Enforced MUST' },
  { value: 'must', label: 'Any MUST' },
  { value: 'should', label: 'MUST and SHOULD' },
  { value: 'none', label: 'None' }
];

const FINDINGS_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'High and Medium' },
  { value: 'none', label: 'None' }
];
const MAP_OPTIONS = [{ value: '', label: PICK }, ...MODEL_EFFORT_OPTIONS];

/** The shown value, clamped whatever was stored. */
export function auditOf(value: unknown): AuditSettings {
  return audit(value, DEFAULT_SETTINGS.audit);
}

function Line({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '7px 0',
        boxShadow: `inset 0 -1px 0 ${color.hairSoft}`
      }}
    >
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: color.inkBody }}>{label}</span>
      {children}
    </div>
  );
}

export function AuditField({
  value,
  onChange,
  editable = true
}: {
  value: unknown;
  onChange: (next: AuditSettings) => void;
  editable?: boolean;
}): React.JSX.Element {
  const current = auditOf(value);
  const put = (patch: Partial<AuditSettings>): void => onChange(audit({ ...current, ...patch }, DEFAULT_SETTINGS.audit));
  const pair = pairOf(current.mapModel, current.mapEffort);
  const said = (options: { value: string; label: string }[], chosen: string): string =>
    options.find((option) => option.value === chosen)?.label ?? chosen;
  return (
    <div>
      <Line label="Audit each hand-back">
        {editable ? (
          <SettingSelect
            value={current.auto}
            options={AUTO_OPTIONS}
            onChange={(next) => put({ auto: next as AuditSettings['auto'] })}
          />
        ) : (
          <span style={{ fontSize: 12.5, color: color.inkMuted }}>{said(AUTO_OPTIONS, current.auto)}</span>
        )}
      </Line>
      <Line label="Change Map drawn with">
        {editable ? (
          <SettingSelect
            value={pair}
            options={MAP_OPTIONS}
            onChange={(next) => {
              const { defaultModel, defaultEffort } = splitPair(next);
              put({ mapModel: next ? defaultModel : '', mapEffort: next ? (defaultEffort ?? '') : '' });
            }}
          />
        ) : (
          <span style={{ fontSize: 12.5, color: color.inkMuted }}>{said(MAP_OPTIONS, pair)}</span>
        )}
      </Line>
      <Line label="Hold the merge on rules">
        {editable ? (
          <SettingSelect
            value={current.blockRules}
            options={RULES_OPTIONS}
            onChange={(next) => put({ blockRules: next as AuditSettings['blockRules'] })}
          />
        ) : (
          <span style={{ fontSize: 12.5, color: color.inkMuted }}>{said(RULES_OPTIONS, current.blockRules)}</span>
        )}
      </Line>
      <Line label="Hold the merge on findings">
        {editable ? (
          <SettingSelect
            value={current.blockFindings}
            options={FINDINGS_OPTIONS}
            onChange={(next) => put({ blockFindings: next as AuditSettings['blockFindings'] })}
          />
        ) : (
          <span style={{ fontSize: 12.5, color: color.inkMuted }}>{said(FINDINGS_OPTIONS, current.blockFindings)}</span>
        )}
      </Line>
    </div>
  );
}
